import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Bell, Bot, CheckCircle2, ExternalLink, Rocket, ShieldAlert, X } from 'lucide-react'
import { api } from '@/lib/api'
import { useCurrentUser } from '@/hooks/useApi'
import { useAppStore } from '@/store'

type OpsEvent = {
  id: string
  event_type: string
  category: string
  severity: string
  source: string
  module: string | null
  worker: string | null
  build_id: string | null
  correlation_id: string | null
  timestamp: string
  payload: string | null
  status: string
  verified: number
}

function readSet(key: string): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(key) || '[]')) } catch { return new Set() }
}

function writeSet(key: string, value: Set<string>) {
  try { localStorage.setItem(key, JSON.stringify([...value].slice(-500))) } catch { /* storage unavailable */ }
}

function titleFor(event: OpsEvent): string {
  const type = event.event_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  if (event.worker) return `${type} · ${event.worker}`
  return type || 'OPS notification'
}

function messageFor(event: OpsEvent): string {
  if (!event.payload) return event.source || event.category
  try {
    const payload = JSON.parse(event.payload)
    return String(payload.summary || payload.message || payload.error || payload.note || payload.result || event.source || event.category).slice(0, 180)
  } catch {
    return event.payload.slice(0, 180)
  }
}

function targetFor(event: OpsEvent): 'auditcentre' | 'activity' | 'knowledge' | 'security' | 'dashboard' {
  if (event.category === 'audit' || /audit/i.test(event.event_type)) return 'auditcentre'
  if (event.category === 'knowledge' || /knowledge/i.test(event.event_type)) return 'knowledge'
  if (event.category === 'security' || /security|threat/i.test(event.event_type)) return 'security'
  if (event.category === 'deployment' || /deploy|build|task|agent/i.test(event.event_type)) return 'activity'
  return 'dashboard'
}

function EventIcon({ event }: { event: OpsEvent }) {
  if (event.severity === 'critical' || event.severity === 'error') return <ShieldAlert className="h-4 w-4 text-red-400" />
  if (event.severity === 'warning') return <AlertTriangle className="h-4 w-4 text-amber-400" />
  if (/deploy|build/i.test(event.event_type)) return <Rocket className="h-4 w-4 text-amber-300" />
  if (/ai|agent|task/i.test(event.event_type)) return <Bot className="h-4 w-4 text-amber-300" />
  return <CheckCircle2 className="h-4 w-4 text-amber-300" />
}

export function NotificationBell() {
  const currentUser = useCurrentUser()
  const setActiveSection = useAppStore(s => s.setActiveSection)
  const [open, setOpen] = useState(false)
  const storageBase = currentUser.user_id || 'anonymous'
  const readKey = `ops-notifications-read:${storageBase}`
  const dismissedKey = `ops-notifications-dismissed:${storageBase}`
  const [readIds, setReadIds] = useState<Set<string>>(() => readSet(readKey))
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => readSet(dismissedKey))

  const query = useQuery({
    queryKey: ['dashboard', 'notifications', currentUser.user_id],
    queryFn: () => api.events(undefined, undefined, undefined, undefined, undefined, 30),
    enabled: !!currentUser.user_id,
    refetchInterval: 15_000,
  })

  const events = useMemo(() => ((query.data?.events || []) as OpsEvent[])
    .filter(event => !/approval/i.test(event.event_type))
    .filter(event => !dismissedIds.has(event.id)), [query.data, dismissedIds])

  const unread = events.filter(event => !readIds.has(event.id)).length

  function markRead(id: string) {
    setReadIds(prev => {
      const next = new Set(prev); next.add(id); writeSet(readKey, next); return next
    })
  }

  function dismiss(id: string) {
    setDismissedIds(prev => {
      const next = new Set(prev); next.add(id); writeSet(dismissedKey, next); return next
    })
  }

  function view(event: OpsEvent) {
    markRead(event.id)
    setOpen(false)
    setActiveSection(targetFor(event))
  }

  function markAllRead() {
    const next = new Set(readIds)
    events.forEach(event => next.add(event.id))
    writeSet(readKey, next)
    setReadIds(next)
  }

  return (
    <div className="relative z-20">
      <button
        onClick={() => setOpen(v => !v)}
        aria-label={unread > 0 ? `Notifications — ${unread} unread` : 'Notifications — none unread'}
        aria-expanded={open}
        className="ops-gold-bell-hitbox relative flex items-center justify-center transition-all"
        title={unread > 0 ? `${unread} unread notification${unread === 1 ? '' : 's'}` : 'Notifications'}
      >
        <svg className="ops-gold-bell-svg" viewBox="0 0 32 32" aria-hidden="true">
          <defs>
            <linearGradient id="opsBellGold" x1="7" y1="5" x2="25" y2="27" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#fff4a8" />
              <stop offset=".25" stopColor="#f7d85a" />
              <stop offset=".58" stopColor="#d79b18" />
              <stop offset="1" stopColor="#7c4d04" />
            </linearGradient>
            <linearGradient id="opsBellLip" x1="8" y1="22" x2="24" y2="27" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#ffe982" />
              <stop offset=".6" stopColor="#c7870d" />
              <stop offset="1" stopColor="#6d4303" />
            </linearGradient>
          </defs>
          <path d="M16 4.4c-4.2 0-7.3 3.3-7.3 7.8v4.2c0 2.7-1 4.9-2.5 6.5-.6.6-.2 1.7.7 1.7h18.2c.9 0 1.3-1.1.7-1.7-1.5-1.6-2.5-3.8-2.5-6.5v-4.2c0-4.5-3.1-7.8-7.3-7.8Z" fill="url(#opsBellGold)" stroke="#6f4606" strokeWidth="1.2"/>
          <path d="M9.2 22.2h13.6c.7 0 1.1.7.8 1.3l-.8 1.6H9.2l-.8-1.6c-.3-.6.1-1.3.8-1.3Z" fill="url(#opsBellLip)" stroke="#785006" strokeWidth=".7"/>
          <path d="M12.9 26.1h6.2c-.4 1.3-1.5 2.2-3.1 2.2s-2.7-.9-3.1-2.2Z" fill="#b77909" stroke="#704704" strokeWidth=".6"/>
          <path d="M11.1 10.7c.6-2.4 2.2-3.9 4.6-4.2" fill="none" stroke="rgba(255,255,255,.72)" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        {unread > 0 && (
          <span className="ops-gold-bell-badge absolute -top-1 -right-1 h-4 min-w-4 px-0.5 rounded-full flex items-center justify-center text-[10px] font-bold leading-none">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setOpen(false)} />
          <div className="ops-notification-panel fixed z-[9999] top-14 right-2 w-[360px] max-w-[calc(100vw-16px)] max-h-[72vh] overflow-hidden rounded-2xl border shadow-2xl backdrop-blur-xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-surface-elevated">
              <div className="flex items-center gap-2"><Bell className="h-4 w-4 text-amber-300" /><span className="text-sm font-semibold">Notifications</span>{unread > 0 && <span className="text-[10px] font-bold text-amber-300">{unread} unread</span>}</div>
              <div className="flex items-center gap-2">
                {unread > 0 && <button onClick={markAllRead} className="text-[10px] text-amber-300 hover:underline">Mark all read</button>}
                <button onClick={() => setOpen(false)}><X className="h-3.5 w-3.5 text-text-muted" /></button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-[62vh]">
              {query.isLoading && <div className="p-6 text-center text-sm text-text-muted">Loading notifications…</div>}
              {!query.isLoading && events.length === 0 && <div className="p-8 text-center text-sm text-text-muted">No notifications</div>}
              {events.map(event => {
                const isUnread = !readIds.has(event.id)
                return (
                  <div key={event.id} className={`px-4 py-3 border-b border-border last:border-0 ${isUnread ? 'bg-amber-400/[0.04]' : ''}`}>
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5"><EventIcon event={event} /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="text-xs font-semibold text-text-primary truncate">{titleFor(event)}</div>
                          {isUnread && <span className="h-1.5 w-1.5 rounded-full bg-amber-300 shrink-0" />}
                        </div>
                        <div className="text-[11px] text-text-muted mt-1 break-words">{messageFor(event)}</div>
                        <div className="text-[10px] text-text-muted mt-1">{new Date(event.timestamp).toLocaleString()} · {event.category}</div>
                        <div className="flex items-center gap-3 mt-2">
                          <button onClick={() => view(event)} className="text-[10px] text-amber-300 hover:underline flex items-center gap-1"><ExternalLink className="h-2.5 w-2.5" />View</button>
                          {!isUnread ? null : <button onClick={() => markRead(event.id)} className="text-[10px] text-text-muted hover:text-text-primary">Mark read</button>}
                          <button onClick={() => dismiss(event.id)} className="text-[10px] text-text-muted hover:text-red-400">Dismiss</button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
